import { Card, CardContent, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDeviceType } from "@hooks/useDeviceType";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { OfferPrice } from "@common/OfferPrice/OfferPrice";
import { useBroadbandLogin } from "@features/broadbandLogin/useBroadbandLogin";
import { useStyle } from "@features/broadbandLogin/style";
import perksImage from "@static/img/broadband/loginPerks.png";
import mobileImage from "@static/img/broadband/loginMobile.png";
import PlusIcon from "@static/icons/Plus.svg";
import { useLoginLogout } from "@hooks/useLoginLogout";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";
import { getOfferPrice } from "@utils/offerUtils";

export const BroadbandLoginPage = () => {
    const { t } = useTranslation("broadbandLogin");
    const { isMobile, isTabletOrDesktop } = useDeviceType();
    const { classes } = useStyle({ isMobile });
    const {
        firstMobileOffer,
        handleAddMobileAndContinue,
        isPageLoading,
        isAddMobileOfferLoading,
        error,
        retry,
    } = useBroadbandLogin();
    const { logIn } = useLoginLogout();
    const mobilePrice = firstMobileOffer && getOfferPrice(firstMobileOffer);

    const renderSectionHeader = (title: string, description: string) => (
        <Stack alignItems="center" justifyContent="center" marginBottom={3} spacing={2}>
            <Typography align="center" className={classes.sectionTitle} variant="h2">
                {title}
            </Typography>
            <Typography align="center" color="secondary" variant="normal">
                {description}
            </Typography>
        </Stack>
    );

    return (
        <HandleErrorAndLoading error={error} isLoading={isPageLoading} retryFetch={retry}>
            <div className={classes.container}>
                <div className={classes.contentWrapper}>
                    <Typography align="center" className={classes.mainTitle} variant="h1">
                        {t("mainTitle")}
                    </Typography>
                    <div className={classes.sectionsWrapper}>
                        <div className={classes.section}>
                            {renderSectionHeader(t("existingCustomer.title"), t("existingCustomer.description"))}
                            <Stack alignItems="center" justifyContent="center" spacing={5}>
                                <LabeledButton
                                    color="primary"
                                    id={BUTTON_IDS.BROADBAND_FLOW.LOGIN_CTA.LOGIN_AS_EXISTING_CUSTOMER}
                                    labelText={t("existingCustomer.button")}
                                    onClick={() => logIn()}
                                >
                                    {t("existingCustomer.button")}
                                </LabeledButton>
                                <Card className={classes.card} elevation={2}>
                                    <CardContent className={classes.cardContentWrapper}>
                                        <div className={classes.cardContent}>
                                            {isTabletOrDesktop && (
                                                <img alt="GoMo Mobile" className={classes.image} src={perksImage} />
                                            )}
                                            <div className={classes.textContainer}>
                                                <Typography align="center" className={classes.cardTitle}>
                                                    {t("existingCustomer.perks.title")}
                                                </Typography>
                                                <div>
                                                    <Typography align="center" color="secondary" variant="small">
                                                        {t("existingCustomer.perks.description")}
                                                    </Typography>
                                                    <Typography align="center" color="secondary" variant="small">
                                                        {t("existingCustomer.perks.subdescription")}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </div>
                        <Divider className={classes.divider} orientation={isMobile ? "horizontal" : "vertical"} />
                        <div className={classes.section}>
                            {renderSectionHeader(t("newCustomer.title"), t("newCustomer.description"))}
                            <Stack alignItems="center" justifyContent="center" spacing={5}>
                                <LabeledButton
                                    color="primary"
                                    disabled={isAddMobileOfferLoading}
                                    id={BUTTON_IDS.BROADBAND_FLOW.LOGIN_CTA.ADD_GOMO_MOBILE_AND_CONTINUE}
                                    labelText={t("newCustomer.button")}
                                    startIcon={<PlusIcon />}
                                    onClick={handleAddMobileAndContinue}
                                >
                                    {t("newCustomer.button")}
                                </LabeledButton>
                                {firstMobileOffer && (
                                    <Card className={classes.card} elevation={2}>
                                        <CardContent className={classes.cardContentWrapper}>
                                            <div className={classes.cardContent}>
                                                {isTabletOrDesktop && (
                                                    <img
                                                        alt="GoMo Customer"
                                                        className={classes.image}
                                                        src={mobileImage}
                                                    />
                                                )}
                                                <div className={classes.mobileCardTextContainer}>
                                                    <div className={classes.mobileCardFlexContainer}>
                                                        <Stack className={classes.mobileCardLeftStack} spacing={1.5}>
                                                            <Typography className={classes.cardTitle} variant="h4">
                                                                {t("newCustomer.mobileCard.title")}
                                                            </Typography>
                                                            <div>
                                                                {firstMobileOffer.usages.slice(0, 2).map((usage) => (
                                                                    <Typography
                                                                        color="secondary"
                                                                        key={usage.description}
                                                                        variant="small"
                                                                    >
                                                                        {usage.description}
                                                                    </Typography>
                                                                ))}
                                                            </div>
                                                        </Stack>
                                                        {mobilePrice && (
                                                            <Stack spacing={0.5}>
                                                                <OfferPrice
                                                                    descriptionFontSize="16px"
                                                                    direction={isMobile ? "row" : "column"}
                                                                    price={mobilePrice}
                                                                    priceFontSize="24px"
                                                                />
                                                            </Stack>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                        </div>
                    </div>
                </div>
            </div>
        </HandleErrorAndLoading>
    );
};
